import './sass/style.scss';
import { initSiteCart } from './js/site-cart.js';
import { initCartAov } from './js/site-cart-aov.js';
import { initArchiveCatalog } from './js/site-archive-catalog.js';
import { initSiteSearch } from './js/site-search.js';
import { initSiteMegaMenu } from './js/site-mega-menu.js';
import { initSiteTopbar } from './js/site-topbar.js';
import { initCheckout } from './js/site-checkout.js';
import { initAccount } from './js/site-account.js';
import { initJournal } from './js/site-journal.js';

const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

const getFocusableElements = (container) =>
    [...container.querySelectorAll(focusableSelector)].filter(
        (element) => {
            const styles = window.getComputedStyle(element);

            return !element.closest('[inert]')
                && element.getClientRects().length > 0
                && styles.display !== 'none'
                && styles.visibility !== 'hidden';
        },
    );

const focusWithoutScroll = (element) => {
    if (!element) {
        return;
    }

    try {
        element.focus({ preventScroll: true });
    } catch {
        element.focus();
    }
};

const initSiteHeader = (header) => {
    if (header.dataset.headerReady === 'true') {
        return;
    }

    const menuToggle = header.querySelector('[data-menu-toggle]');
    const mobilePanel = header.querySelector('[data-mobile-panel]');
    const submenuToggles = [...header.querySelectorAll('[data-submenu-toggle]')];
    const submenuBackButtons = [...header.querySelectorAll('[data-submenu-back]')];
    const desktopQuery = window.matchMedia('(min-width: 1101px)');
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    let menuFocusTimer = 0;
    let submenuFocusTimer = 0;
    let menuScrollPosition = 0;
    let megaController = null;

    if (!menuToggle || !mobilePanel) {
        return;
    }

    const setInert = (element, inert) => {
        element.inert = inert;

        if (inert) {
            element.setAttribute('inert', '');
        } else {
            element.removeAttribute('inert');
        }
    };

    const updateMenuToggleLabel = (open) => {
        const label = menuToggle.querySelector('.screen-reader-text');

        if (!label) {
            return;
        }

        label.textContent = open
            ? menuToggle.dataset.closeLabel || label.textContent
            : menuToggle.dataset.openLabel || label.textContent;
    };

    const closeSubmenu = (item) => {
        window.clearTimeout(submenuFocusTimer);
        item.classList.remove('is-submenu-open');

        const toggle = item.querySelector(':scope > .SiteHeader__menuItemRow [data-submenu-toggle]');
        toggle?.setAttribute('aria-expanded', 'false');

        item.querySelectorAll('.SiteHeader__menuItem.is-submenu-open').forEach((child) => {
            child.classList.remove('is-submenu-open');
            child
                .querySelector(':scope > .SiteHeader__menuItemRow [data-submenu-toggle]')
                ?.setAttribute('aria-expanded', 'false');
        });
    };

    const closeAllSubmenus = () => {
        header.querySelectorAll('.SiteHeader__menuItem.is-submenu-open').forEach(closeSubmenu);
    };

    const updateMobilePanelHeight = () => {
        if (desktopQuery.matches) {
            mobilePanel.style.removeProperty('--site-header-panel-height');
            return;
        }

        const availableHeight = Math.max(0, window.innerHeight - header.getBoundingClientRect().bottom);
        mobilePanel.style.setProperty('--site-header-panel-height', `${availableHeight}px`);
    };

    const setMenuScrollLock = (locked) => {
        if (locked) {
            menuScrollPosition = window.scrollY;
            document.body.style.setProperty(
                '--site-menu-scroll-offset',
                `${menuScrollPosition * -1}px`,
            );
            return;
        }

        document.body.style.removeProperty('--site-menu-scroll-offset');

        const html = document.documentElement;
        const previousBehavior = html.style.scrollBehavior;

        html.style.scrollBehavior = 'auto';
        window.scrollTo(0, menuScrollPosition);
        html.style.scrollBehavior = previousBehavior;
    };

    const setMenuOpen = (open, restoreFocus = true) => {
        const wasOpen = header.classList.contains('is-menu-open');
        window.clearTimeout(menuFocusTimer);
        window.clearTimeout(submenuFocusTimer);

        if (open) {
            megaController?.close({ immediate: true });
            updateMobilePanelHeight();
        }

        if (open && !wasOpen) {
            setMenuScrollLock(true);
        }

        header.classList.toggle('is-menu-open', open);
        document.body.classList.toggle('has-site-menu-open', open);
        menuToggle.setAttribute('aria-expanded', String(open));
        mobilePanel.setAttribute('aria-hidden', String(!open));
        setInert(mobilePanel, !open);
        updateMenuToggleLabel(open);

        if (open) {
            const firstLink = getFocusableElements(mobilePanel)[0];
            menuFocusTimer = window.setTimeout(
                () => focusWithoutScroll(firstLink),
                reducedMotionQuery.matches ? 0 : 220,
            );
        } else {
            closeAllSubmenus();

            if (wasOpen) {
                setMenuScrollLock(false);
            }

            if (wasOpen && restoreFocus) {
                focusWithoutScroll(menuToggle);
            }
        }
    };

    menuToggle.addEventListener('click', () => {
        setMenuOpen(!header.classList.contains('is-menu-open'));
    });

    submenuToggles.forEach((toggle) => {
        toggle.addEventListener('click', () => {
            megaController?.close();
            const item = toggle.closest('.SiteHeader__menuItem');

            if (!item) {
                return;
            }

            const open = !item.classList.contains('is-submenu-open');
            const siblings = [...(item.parentElement?.children || [])].filter(
                (sibling) => sibling !== item && sibling.classList.contains('is-submenu-open'),
            );

            siblings.forEach(closeSubmenu);
            item.classList.toggle('is-submenu-open', open);
            toggle.setAttribute('aria-expanded', String(open));

            if (open && !desktopQuery.matches) {
                const panel = item.querySelector(':scope > [data-submenu-panel]');
                const backButton = panel?.querySelector(':scope > [data-submenu-back]');
                const parentScroller = item.parentElement?.closest('[data-submenu-panel]')
                    || mobilePanel.querySelector('.SiteHeader__mobilePanelInner');

                if (parentScroller) {
                    parentScroller.scrollTop = 0;
                }

                if (panel) {
                    panel.scrollTop = 0;
                }
                window.clearTimeout(submenuFocusTimer);
                submenuFocusTimer = window.setTimeout(
                    () => focusWithoutScroll(backButton),
                    reducedMotionQuery.matches ? 0 : 220,
                );
            }
        });
    });

    submenuBackButtons.forEach((button) => {
        button.addEventListener('click', () => {
            window.clearTimeout(submenuFocusTimer);
            const panel = button.closest('[data-submenu-panel]');
            const item = panel?.parentElement?.closest('.SiteHeader__menuItem');

            if (!item) {
                return;
            }

            const toggle = item.querySelector(
                ':scope > .SiteHeader__menuItemRow [data-submenu-toggle]',
            );

            closeSubmenu(item);
            focusWithoutScroll(toggle);
        });
    });

    mobilePanel.addEventListener('click', (event) => {
        if (!desktopQuery.matches && event.target.closest('a[href]')) {
            setMenuOpen(false, false);
        }
    });

    document.addEventListener('pointerdown', (event) => {
        if (event.target instanceof Element && !event.target.closest('.SiteHeader__menuItem--parent')) {
            closeAllSubmenus();
        }
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            const openSubmenus = [...header.querySelectorAll('.SiteHeader__menuItem.is-submenu-open')];
            const focusedSubmenu = [...openSubmenus].reverse().find((item) => item.contains(document.activeElement));
            const submenuToClose = focusedSubmenu || openSubmenus.at(-1);

            if (submenuToClose) {
                const submenuToggle = submenuToClose.querySelector(
                    ':scope > .SiteHeader__menuItemRow [data-submenu-toggle]',
                );
                closeSubmenu(submenuToClose);
                focusWithoutScroll(submenuToggle);
                return;
            }

            if (header.classList.contains('is-menu-open')) {
                setMenuOpen(false);
            }

            return;
        }

        if (event.key !== 'Tab' || !header.classList.contains('is-menu-open')) {
            return;
        }

        const openPanels = [
            ...mobilePanel.querySelectorAll(
                '.SiteHeader__menuItem.is-submenu-open > [data-submenu-panel]',
            ),
        ];
        const activeLevel = openPanels.at(-1) || mobilePanel;
        const focusable = [menuToggle, ...getFocusableElements(activeLevel)];
        const first = focusable[0];
        const last = focusable.at(-1);

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            focusWithoutScroll(last);
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            focusWithoutScroll(first);
        }
    });

    const handleDesktopChange = () => {
        if (desktopQuery.matches) {
            setMenuOpen(false, false);
        } else if (header.classList.contains('is-menu-open')) {
            updateMobilePanelHeight();
        }
    };

    desktopQuery.addEventListener?.('change', handleDesktopChange);
    window.addEventListener('resize', updateMobilePanelHeight, { passive: true });
    window.addEventListener('orientationchange', updateMobilePanelHeight, { passive: true });

    setInert(mobilePanel, true);
    let searchController = null;
    let cartController = null;
    let aovController = null;
    megaController = initSiteMegaMenu(header, {
        beforeOpen: () => {
            closeAllSubmenus();
            setMenuOpen(false, false);
            searchController?.close({ restoreFocus: false });
            cartController?.close({ restoreFocus: false });
            aovController?.close({ restoreFocus: false, immediate: true });
        },
    });
    cartController = initSiteCart(header, {
        beforeOpen: () => {
            closeAllSubmenus();
            megaController?.close({ immediate: true });
            setMenuOpen(false, false);
            searchController?.close({ restoreFocus: false });
            aovController?.close({ restoreFocus: false, immediate: true });
        },
        onAdded: ({ payload, detail }) => aovController?.open(payload?.aov_offer, {
            trigger: detail?.button || null,
            cart: payload,
        }) === true,
    });
    aovController = initCartAov(header, {
        beforeOpen: () => {
            closeAllSubmenus();
            megaController?.close({ immediate: true });
            setMenuOpen(false, false);
            searchController?.close({ restoreFocus: false });
            cartController?.close({ restoreFocus: false });
        },
        addItem: (body) => cartController?.addItem(body),
        openCart: () => cartController?.open({ refreshCart: false }),
    });
    searchController = initSiteSearch(header, {
        beforeOpen: () => {
            closeAllSubmenus();
            megaController?.close({ immediate: true });
            setMenuOpen(false, false);
            cartController?.close({ restoreFocus: false });
            aovController?.close({ restoreFocus: false, immediate: true });
        },
    });
    header.dataset.headerReady = 'true';
};

const init = () => {
    document.querySelectorAll('[data-topbar-slider]').forEach(initSiteTopbar);
    document.querySelectorAll('[data-site-header]').forEach(initSiteHeader);
    document.querySelectorAll('[data-archive-catalog]').forEach(initArchiveCatalog);
    document.querySelectorAll('[data-checkout-page]').forEach(initCheckout);
    document.querySelectorAll('[data-account-page]').forEach(initAccount);
    document.querySelectorAll('[data-journal-article]').forEach(initJournal);
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
    init();
}
